import {
  useUpdateSolutionViewerAccess,
  useUpdateSolutionViewerDatacenterAccess,
  useUpdateSolutionViewerWaterTreatmentAccess,
  useUpdateDebugModeAccess,
} from '@consigli/hooks';
import { User } from '@consigli/types';
import { useState } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton, ObjectDetailView } from '@/components';
import { errorToast, successToast } from '@/components/toast';

export const UsersActionButton = ({ user }: { user: User }) => {
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const { updateSolutionViewerAccess, isLoading: isSolutionViewerLoading } =
    useUpdateSolutionViewerAccess();
  const { updateSolutionViewerDatacenterAccess, isLoading: isDatacenterLoading } =
    useUpdateSolutionViewerDatacenterAccess();
  const { updateSolutionViewerWaterTreatmentAccess, isLoading: isWaterTreatmentLoading } =
    useUpdateSolutionViewerWaterTreatmentAccess();
  const { updateDebugModeAccess, isLoading: isDebugModeLoading } = useUpdateDebugModeAccess();

  const handleToggleSolutionViewerAccess = async () => {
    const newAccess = !user.solutionViewerAccess;
    try {
      await updateSolutionViewerAccess({
        userId: user.id,
        solutionViewerAccess: newAccess,
      }).unwrap();
      successToast({
        message: newAccess ? 'Solution Viewer access enabled' : 'Solution Viewer access disabled',
      });
    } catch (error) {
      console.error('Failed to update Solution Viewer access:', error);
      errorToast({
        message: 'Failed to update Solution Viewer access',
      });
    }
  };

  const handleToggleDatacenterAccess = async () => {
    const newAccess = !user.solutionViewerDatacenterAccess;
    try {
      await updateSolutionViewerDatacenterAccess({
        userId: user.id,
        solutionViewerDatacenterAccess: newAccess,
      }).unwrap();
      successToast({
        message: newAccess ? 'Datacenter access enabled' : 'Datacenter access disabled',
      });
    } catch (error) {
      console.error('Failed to update Datacenter access:', error);
      errorToast({
        message: 'Failed to update Datacenter access',
      });
    }
  };

  const handleToggleWaterTreatmentAccess = async () => {
    const newAccess = !user.solutionViewerWaterTreatmentAccess;
    try {
      await updateSolutionViewerWaterTreatmentAccess({
        userId: user.id,
        solutionViewerWaterTreatmentAccess: newAccess,
      }).unwrap();
      successToast({
        message: newAccess
          ? 'Wastewater treatment access enabled'
          : 'Wastewater treatment access disabled',
      });
    } catch (error) {
      console.error('Failed to update Wastewater treatment access:', error);
      errorToast({
        message: 'Failed to update Wastewater treatment access',
      });
    }
  };

  const handleToggleDebugModeAccess = async () => {
    const newAccess = !user.debugModeAccess;
    try {
      await updateDebugModeAccess({
        userId: user.id,
        debugModeAccess: newAccess,
      }).unwrap();
      successToast({
        message: newAccess ? 'Debug Mode access enabled' : 'Debug Mode access disabled',
      });
    } catch (error) {
      console.error('Failed to update Debug Mode access:', error);
      errorToast({
        message: 'Failed to update Debug Mode access',
      });
    }
  };

  const actions = [
    {
      label: 'View Details',
      onClick: () => {
        setIsDetailViewOpen(true);
      },
    },
    {
      label: user.solutionViewerAccess
        ? 'Disable Solution Viewer Access'
        : 'Enable Solution Viewer Access',
      onClick: handleToggleSolutionViewerAccess,
      disabled: isSolutionViewerLoading,
      destructive: user.solutionViewerAccess,
    },
    {
      label: user.solutionViewerDatacenterAccess
        ? 'Disable Datacenter Access'
        : 'Enable Datacenter Access',
      onClick: handleToggleDatacenterAccess,
      disabled: isDatacenterLoading,
      destructive: user.solutionViewerDatacenterAccess,
    },
    {
      label: user.solutionViewerWaterTreatmentAccess
        ? 'Disable Wastewater Treatment Access'
        : 'Enable Wastewater Treatment Access',
      onClick: handleToggleWaterTreatmentAccess,
      disabled: isWaterTreatmentLoading,
      destructive: user.solutionViewerWaterTreatmentAccess,
    },
    {
      label: user.debugModeAccess ? 'Disable Debug Mode Access' : 'Enable Debug Mode Access',
      onClick: handleToggleDebugModeAccess,
      disabled: isDebugModeLoading,
      destructive: user.debugModeAccess,
    },
  ];

  return (
    <>
      <ActionButton actions={actions} icon={<BsThreeDots />} />
      <ObjectDetailView
        object={user}
        objectType="User"
        summary="User details"
        isOpen={isDetailViewOpen}
        onOpenChange={setIsDetailViewOpen}
      />
    </>
  );
};
